import { createFileRoute, Link } from "@tanstack/react-router";
import heroItin from "@/assets/hero/hero-itin.jpg.asset.json";

const HERO = {
  src: heroItin.url,
  alt: "Happy foreign national investors celebrating in front of their new single-family US home, one holding up the keys and the other carrying a document folder",
};
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

export const Route = createFileRoute("/business-setup/itin")({
  head: () =>
    buildHead({
      title: "ITIN for Foreign National Investors | Simply Approved",
      description:
        "Foreign national investors without a Social Security number often need an ITIN for US tax filings and banking before closing a business-purpose investment loan.",
      url: "/business-setup/itin",
      type: "article",
      keywords:
        "ITIN for foreign national investors, ITIN for real estate investors, apply for ITIN rental income, Form W-7 real estate, Certifying Acceptance Agent ITIN, ITIN for foreign national loan, ITIN business bank account, ITIN for LLC tax filing, non-US investor tax ID, ITIN for US rental property, foreign national mortgage documentation",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "ITIN for non-US clients", url: "/business-setup/itin" },
      ],
      service: { name: "ITIN Application Assistance for Investors" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: ItinPage,
});

function ItinPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="ITIN for non-US clients"
        subtitle="An Individual Taxpayer Identification Number lets foreign national investors file US tax returns and open many banking relationships tied to US rental property."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"an ITIN"}
        intro={"If you are investing in US property without a Social Security number, an ITIN is how the IRS identifies you. It is what makes US tax filings on rental income possible, and many banks, title companies, and service providers ask for it before they will open an account or complete escrow paperwork."}
        reasons={[
          { title: "Required for US tax filings on rental income", body: "US-source rental income generally has to be reported. Without an ITIN there is no way to file, and withholding rules can get expensive." },
          { title: "Banks and title companies request it", body: "Foreign national investors are routinely asked for an ITIN when opening business banking or completing closing paperwork." },
          { title: "It smooths a foreign national loan file", body: "Our foreign national program does not require US credit, but a consistent tax identity makes documentation and closing far simpler." },
          { title: "It is separate from immigration status", body: "An ITIN is strictly a tax identifier. It does not grant work authorization or change immigration status." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="The basics" title="What an ITIN application usually involves" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "IRS Form W-7 completed and signed",
            "Valid passport, or an accepted combination of identity documents",
            "A qualifying reason for the ITIN — often US-source rental income",
            "A US federal tax return, unless an exception applies",
            "Certified copies or a Certifying Acceptance Agent review of your documents",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Complete IRS Form W-7 with a qualifying reason, usually US-source rental income.",
            "Have a Certifying Acceptance Agent verify your passport so you do not have to mail the original.",
            "Keep the assignment letter with your entity documents for banking, title, and tax filings.",
          ]}
        />
        <PartnerCTA partner={partnerBySlug("theitin")!} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/foreign-national-loans" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Foreign national loan program</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
